import * as Sentry from "@sentry/vue";
import _throttle from 'lodash/throttle';
import { logger } from "./logger";

const superPropertyKeys = {
  version: "_cf_v"
}

const superProperties = {
  version: localStorage.getItem(superPropertyKeys.version)
}

if(import.meta.env.PROD) {

}

const setCannyUser = _throttle((user) => {
  if (window.Canny && user?.name) {
    Canny('identify', {
      appID: '65d2dd49384f842296a38d40',
      user
    });
  }
}, 30 * 1000)


export const setUser = (id, traits = {}) => {
  logger.debug("Identify", id, traits)
  const posthog = window.posthogAnalytics

  if(id) {
    posthog?.identify(id, {
      ...traits,
      firstName: traits?.first_name,
      lastName: traits?.lastName
    })
  } else {
    posthog?.reset()
  }

  if(!import.meta.env.PROD) return
  if(id) {
    Sentry.setUser({ id, email: traits?.email });

    // Intercom boot logic
    const intercomSettings = {
      api_base: "https://api-iam.intercom.io",
      app_id: "nytplqib",
    };

    const bootSettings = { ...intercomSettings };
    bootSettings.user_id = id;
    bootSettings.email = traits?.email;
    bootSettings.name = traits?.name || traits?.first_name;
    bootSettings.hide_default_launcher = true;
    if (traits?.phone) {
      bootSettings.phone = traits?.phone;
    }
    window.Intercom?.("boot", bootSettings);

    setCannyUser({
      email: traits?.email,
      name: traits?.name || traits?.first_name,
      id: id
    })
  } else {
    Sentry.setUser(null)
  }
}

export const setUserTrait = (key, value) => {
  const posthog = window.posthogAnalytics
  if (typeof posthog === 'undefined') return
  const traits = {}
  traits[key] = value
  posthog.setPersonProperties(traits)
}

export const setUserTraits = (traits) => {
  const posthog = window.posthogAnalytics
  if (typeof posthog === 'undefined') return
  posthog.setPersonProperties(traits)
}

export const setSuperProperty = (key, value) => {
  if(Object.keys(superPropertyKeys).includes(key)) {
    superProperties[key] = value
    localStorage.setItem(superPropertyKeys[key], value)
  }
}

function _formatSupabaseError(error) {
  if (!error) return new Error("Unknown error");
  let message = error.message || String(error);
  if (error.details) message += ` | Details: ${error.details}`;
  if (error.hint) message += ` | Hint: ${error.hint}`;
  if (error.code) message += ` | Code: ${error.code}`;
  const formatted = new Error(message);
  if (error.stack) formatted.stack = error.stack;
  return formatted;
}

export const captureError = (err, context = null) => {
  if(!import.meta.env.PROD) return
  const formatted = _formatSupabaseError(err);
  if (context) {
    Sentry.withScope(function (scope) {
      scope.setLevel("error");
      scope.setExtras(context);
      Sentry.captureException(formatted);
    });
  } else {
    Sentry.captureException(formatted);
  }
}

export const captureProblem = (msg, context) => {
  if(!import.meta.env.PROD) return
  Sentry.withScope(function (scope) {
    scope.setLevel("error");
    scope.setExtras(context)
    Sentry.captureMessage(msg);
  });
}

export const track = (event, ctx = {}) => {
  logger.debug("Track event", event, ctx)
  const posthog = window.posthogAnalytics
  const storedProperties = {}
  Object.keys(superPropertyKeys).forEach(function(key) {
    const storedProperty = superProperties[key]
    if (storedProperty) {
      storedProperties[key] = storedProperty
    }
  })
  const mergedProperties = { ...ctx, ...storedProperties }
  posthog?.capture(event, mergedProperties)

  if(!import.meta.env.PROD) return
  window?.Intercom?.('trackEvent', event, ctx)
}

export const page = (name) => {
  logger.debug("Page event", name)
  const posthog = window.posthogAnalytics
  const referringDomain = document.referrer
    ? (() => { try { return new URL(document.referrer).hostname } catch { return '' } })()
    : ''
  posthog?.capture('$pageview', {
    page_name: name,
    $current_url: window.location.href,
    $referrer: document.referrer,
    $referring_domain: referringDomain,
  })
}

const REWARDFUL_STORAGE_KEY = 'rewardful_referral'

export const saveReferralCode = (code) => {
  if (code && typeof code === 'string' && code.trim()) {
    try {
      localStorage.setItem(REWARDFUL_STORAGE_KEY, code.trim())
    } catch (e) {}
  }
}

export const getReferralCode = () => {
  const fromRewardful = typeof window !== 'undefined' ? window.Rewardful?.referral : null
  if (fromRewardful && typeof fromRewardful === 'string' && fromRewardful.trim()) {
    saveReferralCode(fromRewardful)
    return fromRewardful.trim()
  }
  try {
    const fromUrl = new URLSearchParams(window.location.search).get('referral')
    if (fromUrl && fromUrl.trim()) {
      saveReferralCode(fromUrl)
      return fromUrl.trim()
    }
    const stored = localStorage.getItem(REWARDFUL_STORAGE_KEY)
    if (stored && stored.trim()) {
      return stored.trim()
    }
  } catch (e) {}
  return null
}

export const referralInit = () => {
  try {
    const fromUrl = new URLSearchParams(window.location.search).get('referral')
    if (fromUrl) saveReferralCode(fromUrl)
  } catch (e) {}

  if (typeof window !== 'undefined' && window.rewardful) {
    window.rewardful('ready', function () {
      if (Rewardful?.referral) {
        saveReferralCode(Rewardful.referral)
        logger.debug('Current referral ID: ', Rewardful.referral);
        if (import.meta.env.PROD) {
          setTimeout(() => {
            track("referral", {identifier: Rewardful.referral})
          }, 1000)
        }
      } else {
        logger.debug('No referral present.');
      }
    });
  }
}

export const referralTrackLead = (id) => {
  if(!import.meta.env.PROD) return
}

export const referralTrackConversion = (id) => {
  if(!import.meta.env.PROD) return
}
